// src/pages/AdoptChat.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const ANTHROPIC_API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY;

export default function AdoptChat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [adopter, setAdopter] = useState(null);
  const messagesEnd = useRef(null);

  useEffect(() => {
    getDoc(doc(db, "cats", id)).then(snap => {
      if (snap.exists()) {
        const catData = { id: snap.id, ...snap.data() };
        setCat(catData);
        // Start conversation
        const greeting = `Hi! 🐾 I'm so glad you're interested in adopting **${catData.name}**!\n\nTo help make the best match, I'll ask you a few quick questions. It only takes about 2 minutes.\n\nFirst — what's your name?`;
        setMessages([{ role: "assistant", content: greeting }]);
      }
    });
  }, [id]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const systemPrompt = cat ? `You are a friendly cat adoption screening assistant for Purrfect Match shelter.
You are screening a potential adopter for: ${cat.name}, a ${cat.age} ${cat.gender} ${cat.breed || "mixed breed"}.
Cat description: ${cat.description}
Cat traits: ${(cat.traits || []).join(", ")}

Your job: Have a warm, conversational screening interview. Ask these questions naturally (not all at once):
1. Their name
2. Their living situation (house/apartment, own/rent)
3. Whether their home is pet-friendly (if renting)
4. Who lives with them (family members, other pets)
5. Their experience with cats
6. Why they want to adopt this specific cat
7. Their WhatsApp number (to connect with the owner)

Keep it friendly and conversational. After collecting all info including WhatsApp number, say exactly:
"SCREENING_COMPLETE" followed by a JSON object on a new line like:
{"name":"...","phone":"...","living":"...","experience":"...","reason":"...","otherPets":"...","household":"..."}

Do not ask more than one question at a time. Be encouraging and warm.` : "";

  async function sendMessage() {
    if (!input.trim() || loading || done) return;
    const userMsg = input.trim();
    setInput("");
    const newMessages = [...messages, { role: "user", content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: systemPrompt,
          messages: newMessages.map(m => ({ role: m.role, content: m.content }))
        })
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "Sorry, something went wrong. Please try again.";

      // Check if screening is complete
      if (reply.includes("SCREENING_COMPLETE")) {
        const jsonMatch = reply.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            const adopterData = JSON.parse(jsonMatch[0]);
            setAdopter(adopterData);
            await saveApplication(adopterData);
            setDone(true);
            const finalMsg = reply.split("SCREENING_COMPLETE")[0].trim() ||
              `Thank you! 🎉 Your application for **${cat.name}** has been submitted. The owner will be notified on WhatsApp and will get back to you soon!`;
            setMessages([...newMessages, { role: "assistant", content: finalMsg }]);
          } catch (e) {
            console.error("JSON parse error", e);
          }
        }
      } else {
        setMessages([...newMessages, { role: "assistant", content: reply }]);
      }
    } catch (err) {
      console.error(err);
      setMessages([...newMessages, {
        role: "assistant",
        content: "Oops! Something went wrong. Please try again."
      }]);
    }
    setLoading(false);
  }

  async function saveApplication(adopterData) {
    try {
      // Save to Firestore
      const appRef = await addDoc(collection(db, "applications"), {
        catId: id,
        catName: cat.name,
        ownerPhone: cat.ownerPhone,
        adopter: adopterData,
        conversation: messages,
        status: "pending",
        createdAt: serverTimestamp()
      });

      // Notify owner via your backend/Cloud Function
      await fetch("/api/notify-owner", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          applicationId: appRef.id,
          catName: cat.name,
          ownerPhone: cat.ownerPhone,
          adopter: adopterData
        })
      });
    } catch (err) {
      console.error("Save application error:", err);
    }
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  if (!cat) return <div style={styles.loading}><div className="spinner"/></div>;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Cat mini header */}
        <div style={styles.catHeader}>
          <div style={styles.catThumb}>
            {cat.photos?.[0]
              ? <img src={cat.photos[0]} alt={cat.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              : <span style={{fontSize:28}}>🐱</span>}
          </div>
          <div>
            <h2 style={styles.catHeaderName}>Adopting {cat.name}</h2>
            <p style={styles.catHeaderSub}>{cat.age} · {cat.breed || "Mixed breed"} · AI Screening Chat</p>
          </div>
          <button onClick={()=>navigate(`/cat/${id}`)} style={styles.backBtn}>← Back</button>
        </div>

        {/* Chat */}
        <div style={styles.chatCard}>
          <div style={styles.messages} ref={messagesEnd}>
            {messages.map((m, i) => (
              <div key={i} style={{...styles.msgRow, justifyContent: m.role==="user" ? "flex-end":"flex-start"}}>
                {m.role === "assistant" && <div style={styles.botAvatar}>🐱</div>}
                <div style={{
                  ...styles.bubble,
                  ...(m.role === "user" ? styles.bubbleUser : styles.bubbleBot)
                }}>
                  {renderText(m.content)}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{...styles.msgRow, justifyContent:"flex-start"}}>
                <div style={styles.botAvatar}>🐱</div>
                <div style={styles.bubbleBot}>
                  <div style={styles.dots}>
                    <span/><span/><span/>
                  </div>
                </div>
              </div>
            )}
            {done && (
              <div style={styles.doneCard}>
                <p style={{fontSize:32}}>🎉</p>
                <p style={{fontFamily:"'Playfair Display',serif", fontSize:20, color:"var(--deep)", marginTop:8}}>Application Submitted!</p>
                <p style={{color:"var(--muted)", fontSize:14, marginTop:6}}>The owner has been notified on WhatsApp. Sit tight!</p>
                <button onClick={()=>navigate("/")} style={{...styles.doneBtn, marginTop:16}}>Browse More Cats</button>
              </div>
            )}
            <div ref={messagesEnd}/>
          </div>

          {!done && (
            <div style={styles.inputArea}>
              <textarea
                value={input}
                onChange={e=>setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Type your answer…"
                style={styles.textarea}
                rows={1}
                disabled={loading}
              />
              <button onClick={sendMessage} disabled={loading || !input.trim()} style={styles.sendBtn}>
                {loading ? <div className="spinner" style={{width:18,height:18}}/> : "→"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function renderText(text) {
  // Simple bold markdown support
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") ? <strong key={i}>{p.slice(2,-2)}</strong> : p
  );
}

const styles = {
  page: { minHeight:"100vh", padding:"32px 0 80px", background:"var(--cream)" },
  container: { maxWidth:680, margin:"0 auto", padding:"0 20px" },
  loading: { padding:80, textAlign:"center" },

  catHeader: {
    display:"flex", alignItems:"center", gap:14, marginBottom:24,
    background:"#fff", borderRadius:16, padding:"14px 20px",
    boxShadow:"var(--shadow-s)",
  },
  catThumb: { width:52, height:52, borderRadius:50, overflow:"hidden", background:"var(--warm)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  catHeaderName: { fontFamily:"'Playfair Display',serif", fontSize:20, color:"var(--deep)" },
  catHeaderSub: { fontSize:13, color:"var(--muted)", marginTop:2 },
  backBtn: { marginLeft:"auto", background:"none", border:"none", color:"var(--muted)", cursor:"pointer", fontSize:14 },

  chatCard: {
    background:"var(--warm)", borderRadius:20,
    boxShadow:"var(--shadow-m)",
    overflow:"hidden",
    display:"flex", flexDirection:"column",
    height:540,
    border:"1px solid rgba(192,98,42,0.1)",
  },
  messages: {
    flex:1, overflowY:"auto", padding:"24px 20px",
    display:"flex", flexDirection:"column", gap:16,
    scrollBehavior:"smooth",
  },
  msgRow: { display:"flex", alignItems:"flex-end", gap:8 },
  botAvatar: { width:32, height:32, borderRadius:50, background:"var(--rust)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 },
  bubble: { maxWidth:"78%", padding:"12px 16px", borderRadius:18, fontSize:15, lineHeight:1.6 },
  bubbleBot: { background:"#fff", color:"var(--text)", borderBottomLeftRadius:4, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" },
  bubbleUser: { background:"var(--rust)", color:"#fff", borderBottomRightRadius:4 },

  dots: {
    display:"flex", gap:5, alignItems:"center",
  },
  inputArea: {
    padding:"14px 16px", background:"rgba(255,255,255,0.7)",
    borderTop:"1px solid rgba(192,98,42,0.1)",
    display:"flex", gap:10, alignItems:"flex-end",
  },
  textarea: {
    flex:1, border:"1.5px solid rgba(192,98,42,0.2)", borderRadius:14,
    padding:"10px 14px", fontSize:15, color:"var(--text)",
    background:"#fff", resize:"none", outline:"none",
    fontFamily:"'Outfit',sans-serif", lineHeight:1.5,
    minHeight:44, maxHeight:120,
  },
  sendBtn: {
    width:44, height:44, borderRadius:14, background:"var(--rust)",
    color:"#fff", fontSize:20, border:"none", cursor:"pointer",
    display:"flex", alignItems:"center", justifyContent:"center",
    flexShrink:0, transition:"all 0.2s",
  },
  doneCard: {
    background:"#fff", borderRadius:16, padding:28, textAlign:"center",
    boxShadow:"var(--shadow-s)", margin:"8px 0",
  },
  doneBtn: {
    background:"var(--rust)", color:"#fff", border:"none",
    padding:"12px 28px", borderRadius:50, fontSize:15,
    cursor:"pointer", fontFamily:"'Outfit',sans-serif",
  },
};
